import { createClient } from '@supabase/supabase-js';
import { logger } from '@/lib/logger';
// import { Database } from '@/types/supabase';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Debug logging (only in development)
if (import.meta.env.DEV) {
  logger.debug('Supabase Configuration', {
    component: 'supabase',
    data: {
      url: supabaseUrl ? `SET (${supabaseUrl.substring(0, 30)}...)` : 'NOT SET',
      anonKey: supabaseAnonKey ? `SET (length: ${supabaseAnonKey.length})` : 'NOT SET',
    },
  });
}

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// Ensure we're using HTTPS for Supabase URL
const normalizedSupabaseUrl = supabaseUrl.replace(/^http:/, 'https:');

export const supabase = createClient(normalizedSupabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true
  },
  global: {
    headers: {
      'X-Client-Info': 'kirana360@1.0.0'
    }
  },
  db: {
    schema: 'public'
  },
  realtime: {
    params: {
      eventsPerSecond: 2
    }
  }
});

supabase.auth.onAuthStateChange((event, session) => {
  logger.info('Auth event received', { component: 'supabase', data: { event } });
  if (event === 'TOKEN_REFRESHED') {
    logger.info('Token refreshed', { component: 'supabase', data: { userId: session?.user?.id } });
  }
});

// Exponential backoff helper with maximum delay
const getBackoffDelay = (retryCount: number): number => {
  return Math.min(1000 * Math.pow(2, retryCount), 10000);
};

// Enhanced error handling wrapper
export const handleSupabaseError = async (error: any, retryCount = 3): Promise<never> => {
  logger.error('Supabase error', { error, component: 'supabase' });
  
  // Handle specific database errors
  if (error.code === '23502') {
    throw new Error('Required field missing');
  }
  
  if (error.code === 'PGRST116') {
    throw new Error('Invalid reference');
  }

  // Network error handling with improved retry logic
  if (error.message === 'Failed to fetch' || error.message?.includes('NetworkError')) {
    if (retryCount > 0) {
      const delay = getBackoffDelay(3 - retryCount);
      logger.info(`Retrying request in ${delay}ms`, { component: 'supabase', data: { retryCount } });
      await new Promise(resolve => setTimeout(resolve, delay));
      // Remove the retry call since it causes type issues
      throw new Error('Network error - retry needed');
    }
    
    // Enhanced error detection for specific network issues
    if (error.cause?.message?.includes('CORS')) {
      throw new Error(`CORS error - Please check Supabase CORS configuration for origin: ${window.location.origin}`);
    }

    if (error.cause?.message?.includes('DNS')) {
      throw new Error('DNS resolution failed - Please check your network connection and Supabase URL configuration');
    }

    if (error.cause?.message?.includes('SSL') || error.cause?.message?.includes('certificate')) {
      throw new Error('SSL/TLS error - Please check your connection security settings');
    }

    throw new Error(`Network error - Unable to connect to Supabase at ${normalizedSupabaseUrl}. Please check your connection and try again.`);
  }

  // Authentication errors
  if (error.message?.includes('JWT')) {
    throw new Error('Authentication error - Please sign in again');
  }

  // Rate limiting
  if (error.status === 429) {
    throw new Error('Too many requests - Please try again later');
  }

  // Generic error handler
  throw new Error(error.message || 'An unexpected error occurred while connecting to the database');
};

// Retry wrapper for Supabase operations with improved error handling
const withRetry = async <T>(operation: () => Promise<T>, retries = 3): Promise<T> => {
  try {
    return await operation();
  } catch (error) {
    if (retries > 0) {
      const delay = getBackoffDelay(3 - retries);
      logger.info(`Retrying operation in ${delay}ms`, { component: 'supabase', data: { retries } });
      await new Promise(resolve => setTimeout(resolve, delay));
      return withRetry(operation, retries - 1);
    }
    // If no retries left, re-throw the error
    throw error;
  }
};